import React, { useEffect, useState, useCallback } from 'react';
import { analyticsApi } from '../lib/api';
import { Users, MessageSquare, TrendingUp, Star, Filter } from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  totalSessions: number;
  totalMessages: number;
  uniqueQuestions: number;
  recentSessions: number;
  sessionsWithFeedback: number;
  averageRating: number;
  topCategories: Array<{
    id: number;
    name: string;
    sessionCount: number;
  }>;
}

interface Filters {
  session_type: string;
  purpose: string;
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({ session_type: '', purpose: '' });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const activeFilters: { session_type?: string; purpose?: string } = {};
      if (filters.session_type) activeFilters.session_type = filters.session_type;
      if (filters.purpose) activeFilters.purpose = filters.purpose;
      const response = await analyticsApi.getDashboard(Object.keys(activeFilters).length > 0 ? activeFilters : undefined);
      setStats(response.analytics);
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 dark:bg-gray-700 h-24 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'bg-blue-500',
      description: 'Unique users who participated'
    },
    {
      title: 'Total Sessions',
      value: stats?.totalSessions || 0,
      icon: MessageSquare,
      color: 'bg-green-500',
      description: 'Chat sessions created'
    },
    {
      title: 'Total Messages',
      value: stats?.totalMessages || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
      description: 'Messages exchanged'
    },
    {
      title: 'Average Rating',
      value: stats?.averageRating ? stats.averageRating.toFixed(1) : '0',
      icon: Star,
      color: 'bg-yellow-500',
      description: 'User satisfaction rating'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard Overview
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Welcome to VMS Admin Dashboard. Monitor your chat application analytics.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-dark-700">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Filter className="h-4 w-4" />
            Filters
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Session Type:</label>
            <select
              value={filters.session_type}
              onChange={(e) => setFilters(f => ({ ...f, session_type: e.target.value }))}
              className="text-sm border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="user">User</option>
              <option value="vmp">VMP</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Purpose:</label>
            <select
              value={filters.purpose}
              onChange={(e) => setFilters(f => ({ ...f, purpose: e.target.value }))}
              className="text-sm border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="general">General</option>
              <option value="objective-generation">Objective Generation</option>
              <option value="assessment">Assessment</option>
            </select>
          </div>
          {(filters.session_type || filters.purpose) && (
            <button
              onClick={() => setFilters({ session_type: '', purpose: '' })}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {card.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {card.value}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {card.description}
                </p>
              </div>
              <div className={`${card.color} p-3 rounded-lg`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-dark-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Sessions (Last 7 days)
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {stats?.recentSessions || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Sessions with Feedback
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {stats?.sessionsWithFeedback || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Unique Questions
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {stats?.uniqueQuestions || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Top Categories */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-dark-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top Categories
          </h3>
          <div className="space-y-3">
            {stats?.topCategories?.map((category, index) => (
              <div key={category.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-dark-700 px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {category.name}
                  </span>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {category.sessionCount} sessions
                </span>
              </div>
            )) || (
              <p className="text-sm text-gray-500 dark:text-gray-500">
                No category data available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;